import React from 'react';
import useAnalytics from 'helpers/useAnalytics';
import _get from 'lodash/get';
import _isEqual from 'lodash/isEqual';
import _differenceBy from 'lodash/differenceBy';
import _isEmpty from 'lodash/isEmpty';
import makeStyles from '@material-ui/core/styles/makeStyles';
import { Theme } from 'styles/ThemeTyping';
import { useDispatch } from 'react-redux';
import { useData, useStatus } from 'resift';
import { useHistory, Prompt, useLocation } from 'react-router-dom';
import showSnackbar from 'helpers/showSnackbar';
import makeGetUserFieldsFetch from 'fetches/makeGetUserFieldsFetch';
import makeGetRole from 'fetches/rolesAndPermissions/makeGetRole';
import makeUpdateRoleMembers from 'fetches/rolesAndPermissions/makeUpdateRoleMembers';
import makeUpdateRole from 'fetches/rolesAndPermissions/makeUpdateRole';
import makePostFilterGetUserCount from 'fetches/makePostFilterGetUserCount';
import createSearchFilterQueryParams from 'fetches/rolesAndPermissions/createSearchFilterQueryParams';
import buildFilterFromAPI from 'pages/RolesAndPermissions/helpers/buildFilterFromAPI';
import transformToApiFilter from 'pages/RolesAndPermissions/helpers/toApiFilter';
import { MATCH_ALL_FILTER } from 'pages/RolesAndPermissions/consts';
import { HOME_URL, ROLES_AND_PERMISSIONS_URL } from 'components/Router/consts';
import * as Types from 'pages/RolesAndPermissions/typings';
import * as Sift from '@sift/types';

import { Formik, Form } from 'formik';
import Dialog from '@material-ui/core/Dialog';
import DialogTitle from '@material-ui/core/DialogTitle';
import Button from '@material-ui/core/Button';
import Box from '@material-ui/core/Box';
import Divider from '@material-ui/core/Divider';
import Typography from '@material-ui/core/Typography';
import AppBar from '@material-ui/core/AppBar';
import Toolbar from '@material-ui/core/Toolbar';
import Paper from '@material-ui/core/Paper';
import CancelButton from 'components/Page/CancelButton';
import EditDisabledNotice from 'pages/RolesAndPermissions/molecules/EditDisabledNotice';
import Filters from 'pages/RolesAndPermissions/molecules/Filters';
import Page from 'components/Page/newComponents';
import PageFormToolbar from 'components/Page/newComponents/PageFormToolbar';
import ManuallyManagedMembers from './ManuallyManagedMembers';

interface Props {
  roleMembersResponse: Types.RoleMembersResponse;
  role: Types.Role;
}

function ManageRoleMembersPage({ role, roleMembersResponse }: Props) {
  const classes = useStyles();
  const analytics = useAnalytics();
  const dispatch = useDispatch();
  const history = useHistory();
  const location = useLocation();
  const fromEditUserId = _get(location, ['state', 'fromEditUserId']);
  const roleId = _get(role, 'id', '');
  const getRole = makeGetRole(roleId);
  const getUserCount = makePostFilterGetUserCount();
  const membershipFilterFromAPI = _get(role, 'membershipFilter');
  const apiFilterIsEveryone = _isEqual(membershipFilterFromAPI, MATCH_ALL_FILTER);

  const getUserFields = makeGetUserFieldsFetch();
  const userFieldsData = useData(getUserFields);
  const userFields: Sift.Field[] = _get(userFieldsData, 'results', []);
  const membershipFilter = buildFilterFromAPI(membershipFilterFromAPI, userFields);

  const updateRoleMembers = makeUpdateRoleMembers(roleId);
  const updateRole = makeUpdateRole(roleId);
  const updateMembersStatus = useStatus(updateRoleMembers);
  const updateFiltersStatus = useStatus(updateRole);

  showSnackbar({
    status: updateMembersStatus,
    message: 'Successfully saved member edits.',
    variant: 'success',
    errorMessage: 'An error occurred saving member edits',
  });

  showSnackbar({
    status: updateFiltersStatus,
    message: 'Successfully saved filter edits.',
    variant: 'success',
    errorMessage: 'An error occurred saving filter edits',
  });

  const handleBack = () => {
    history.push(`${HOME_URL}${ROLES_AND_PERMISSIONS_URL}/roles/${roleId}`, {
      fromEditUserId,
    });
  };

  const initialValues = {
    outerOperator: (membershipFilter.operator || 'and') as Types.Operator,
    everyoneSelected: apiFilterIsEveryone,
    filterGroups: _get(membershipFilter, 'filters', []),
    roleMembers: _get(roleMembersResponse, 'results', []),
  };

  return (
    <Dialog open fullScreen aria-labelledby="manage-role-members">
      <AppBar className={classes.appBar}>
        <Toolbar>
          <Typography id="manage-role-members" variant="h6" className={classes.flex}>
            Manage Role Members
          </Typography>
          <Button color="inherit" className={classes.cancelButton} onClick={handleBack}>
            Close
          </Button>
        </Toolbar>
      </AppBar>
      <Page classes={{ root: classes.pageRoot }}>
        {!_get(role, 'membersEditable') && (
          <EditDisabledNotice text="Membership cannot be edited for this default role. You can still view membership information below." />
        )}

        <Formik initialValues={initialValues} onSubmit={() => {}} validate={() => {}}>
          {props => {
            const { dirty, values, setFieldValue, resetForm } = props;
            const { filterGroups, outerOperator, everyoneSelected, roleMembers } = values;

            const handleSetOuterOperator = (value: Types.Operator) => {
              setFieldValue('outerOperator', value);
            };
            const handleSetFilterGroups = (value: Array<any>) => {
              setFieldValue('filterGroups', value);
            };
            const handleSetEveryoneSelected = (value: boolean) => {
              analytics.track('Roles/Permissions - Create Role - Add Everyone Clicked', {
                category: 'AD - Roles/Permissions',
              });

              setFieldValue('everyoneSelected', value);
            };
            const handleSetRoleMembers = (value: Array<any>) => {
              setFieldValue('roleMembers', value);
            };

            const handleSaveManuallyManagedMembers = async () => {
              const _destroyActions = _differenceBy(
                initialValues.roleMembers,
                roleMembers,
                'personId',
              );

              const destroyActions = !_destroyActions.length
                ? []
                : _destroyActions.map(item => ({
                    type: 'destroy' as 'create' | 'destroy',
                    id: item.personId,
                  }));

              const _createActions = _differenceBy(
                roleMembers,
                initialValues.roleMembers,
                'personId',
              );

              const createActions = !_createActions.length
                ? []
                : _createActions.map(item => ({
                    type: 'create' as 'create' | 'destroy',
                    id: item.personId,
                    data: { exclude: item.exclude },
                  }));

              const allActions = [...destroyActions, ...createActions];
              !_isEmpty(allActions) && (await dispatch(updateRoleMembers(allActions)));
            };

            const handleUpdateFilteredUsers = (
              latestFilterGroups: Array<Types.FilterGroup> = filterGroups,
              latestOutOperator: 'and' | 'or' = outerOperator,
            ) => {
              const latestMembershipFilter = transformToApiFilter(
                latestFilterGroups,
                latestOutOperator,
              );

              dispatch(getUserCount(latestMembershipFilter));
              dispatch(
                createSearchFilterQueryParams({
                  filter: latestMembershipFilter,
                  name: `${role.name} members`,
                }),
              );
            };

            const handleSaveFilters = async () => {
              if (
                _isEqual(everyoneSelected, initialValues.everyoneSelected) &&
                _isEqual(outerOperator, initialValues.outerOperator) &&
                _isEqual(filterGroups, initialValues.filterGroups)
              ) {
                return;
              }

              if (everyoneSelected) {
                await dispatch(
                  updateRole({
                    membershipFilter: MATCH_ALL_FILTER,
                  }),
                );
              } else {
                const membershipFilter = transformToApiFilter(filterGroups, outerOperator);
                await dispatch(updateRole({ membershipFilter }));
              }
            };

            const handleSave = async () => {
              await handleSaveManuallyManagedMembers();
              await handleSaveFilters();
              analytics.track('Roles/Permissions - Create Role - Manage Members Saved', {
                category: 'AD - Roles/Permissions',
              });

              await dispatch(getRole());
              handleBack();
            };

            return (
              <Form>
                <Paper className={classes.paper} elevation={2}>
                  <div className={classes.section}>
                    <DialogTitle className={classes.title}>
                      Role Members
                      <Typography gutterBottom className={classes.subtitle}>
                        Use one or more of the sections below to assign members to this role, either
                        explicitly or dynamically via a filter.
                      </Typography>
                    </DialogTitle>
                  </div>

                  <Divider className={classes.divider} />

                  <div className={classes.section}>
                    <Box>
                      <Typography className={classes.sectionTitle}>
                        Assign Dynamic Filters
                      </Typography>
                      <Typography gutterBottom className={classes.subtitle}>
                        Create a filter to automatically include people as members of the role if
                        they meet the filter conditions. This allows you to automatically
                        include/exclude members as they are onboarded or as their information
                        changes.
                      </Typography>
                    </Box>
                    <Filters
                      filterGroups={filterGroups}
                      shouldDisableAddFilter={everyoneSelected}
                      outerOperator={outerOperator}
                      role={role}
                      pageType="members"
                      onSetOuterOperator={handleSetOuterOperator}
                      everyoneSelected={everyoneSelected}
                      onSetFilterGroups={handleSetFilterGroups}
                      onSetEveryoneSelected={handleSetEveryoneSelected}
                      onUpdateFilteredUsers={handleUpdateFilteredUsers}
                    />
                  </div>

                  <div className={classes.section}>
                    <ManuallyManagedMembers
                      roleMembers={roleMembers}
                      roleId={roleId}
                      onUpdate={handleSetRoleMembers}
                      disabled={!_get(role, 'membersEditable')}
                    />
                  </div>
                </Paper>
                <PageFormToolbar dirty={dirty} rightSide>
                  <CancelButton onClick={resetForm} />
                  <Button
                    onClick={handleSave}
                    disabled={!dirty}
                    variant="contained"
                    color="secondary"
                  >
                    Save and Next
                  </Button>
                </PageFormToolbar>
                <Prompt
                  when={dirty}
                  message={
                    'Your work is not saved, or you have errors that need to be addressed! Are you sure you want to leave?'
                  }
                />
              </Form>
            );
          }}
        </Formik>
      </Page>
    </Dialog>
  );
}

const useStyles = makeStyles((theme: Theme) => ({
  root: {},
  pageRoot: {
    display: 'flex',
    flex: '1 1 auto',
    height: 'calc(100vh - 64px)',
  },
  paper: {
    flexGrow: 1,
    maxWidth: 900,
    marginRight: theme.spacing(3),
    width: '100%',
    paddingBottom: theme.spacing(4),
  },
  section: {
    paddingLeft: theme.spacing(4),
    paddingRight: theme.spacing(4),
  },
  appBar: {
    position: 'relative',
    backgroundColor: '#2f2f32',
  },
  flex: {
    flex: 1,
  },
  cancelButton: {
    marginRight: theme.spacing(1),
  },
  title: {
    padding: 0,
  },
  subtitle: {
    ...theme.typography.body2,
    color: theme.colors.gray1,
  },
  sectionTitle: {
    fontWeight: theme.typography.fontWeight.semiBold,
  },
  divider: {
    margin: 0,
    marginTop: theme.spacing(2),
    marginBottom: theme.spacing(2),
  },
}));

export default ManageRoleMembersPage;
